import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import { supabase } from '../../../lib/supabase';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

const StatCard = ({ icon, label, value, sub, color = 'primary' }) => {
  const colorMap = {
    primary: 'bg-primary/10 text-primary',
    success: 'bg-success/10 text-success',
    warning: 'bg-warning/10 text-warning',
    error: 'bg-error/10 text-error',
  };
  return (
    <div className="bg-card rounded-lg border border-border p-4 flex items-center gap-4">
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${colorMap?.[color]}`}>
        <Icon name={icon} size={22} color="currentColor" />
      </div>
      <div>
        <p className="text-2xl font-heading font-bold text-foreground">{value}</p>
        <p className="text-sm font-medium text-foreground">{label}</p>
        {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
      </div>
    </div>
  );
};

const AssignmentAnalytics = ({ technicians = [], assignments = [] }) => {
  const [techStats, setTechStats] = useState([]);
  const [locationStats, setLocationStats] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalyticsData();
  }, [technicians]);

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      // Fetch ticket_technicians to get assignment counts per technician
      const { data: ttData } = await supabase
        ?.from('ticket_technicians')
        ?.select('technician_id, tickets(ticket_number, ticket_priorities(name), ticket_statuses(name), customer_locations(well_name, location))');

      // Build per-technician stats
      const statsMap = {};
      technicians?.forEach(tech => {
        statsMap[tech?.id] = {
          id: tech?.id,
          name: tech?.name,
          avatar: tech?.avatar,
          avatarAlt: tech?.avatarAlt,
          status: tech?.status,
          rating: tech?.rating || 0,
          totalAssignments: 0,
          completedAssignments: 0,
          activeAssignments: 0,
          urgentAssignments: 0,
        };
      });

      ttData?.forEach(row => {
        const techId = row?.technician_id;
        if (!statsMap?.[techId]) return;
        statsMap[techId].totalAssignments += 1;
        const status = row?.tickets?.ticket_statuses?.name?.toLowerCase();
        const priority = row?.tickets?.ticket_priorities?.name?.toLowerCase();
        if (status === 'completed' || status === 'closed') {
          statsMap[techId].completedAssignments += 1;
        } else {
          statsMap[techId].activeAssignments += 1;
        }
        if (priority === 'urgent') {
          statsMap[techId].urgentAssignments += 1;
        }
      });

      const statsArr = Object.values(statsMap)?.sort((a, b) => b?.totalAssignments - a?.totalAssignments);
      setTechStats(statsArr);

      // Location distribution from ticket data
      const locMap = {};
      ttData?.forEach(row => {
        const wellName = row?.tickets?.customer_locations?.well_name;
        if (wellName) {
          locMap[wellName] = (locMap?.[wellName] || 0) + 1;
        }
      });
      const locArr = Object.entries(locMap)?.map(([name, value]) => ({ name, value }))?.sort((a, b) => b?.value - a?.value)?.slice(0, 5);
      setLocationStats(locArr);
    } catch (err) {
      console.error('Analytics fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const totalAssignments = techStats?.reduce((s, t) => s + t?.totalAssignments, 0);
  const totalCompleted = techStats?.reduce((s, t) => s + t?.completedAssignments, 0);
  const totalActive = techStats?.reduce((s, t) => s + t?.activeAssignments, 0);
  const totalUrgent = techStats?.reduce((s, t) => s + t?.urgentAssignments, 0);
  const avgRating = technicians?.length
    ? (technicians?.reduce((s, t) => s + (t?.rating || 0), 0) / technicians?.length)?.toFixed(1)
    : '0.0';

  const barData = techStats?.slice(0, 6)?.map(t => ({
    name: t?.name?.split(' ')?.[0],
    Active: t?.activeAssignments,
    Completed: t?.completedAssignments,
    Urgent: t?.urgentAssignments,
  }));

  const statusPieData = [
    { name: 'Active', value: totalActive },
    { name: 'Completed', value: totalCompleted },
    { name: 'Urgent', value: totalUrgent },
  ]?.filter(d => d?.value > 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Icon name="Loader" size={32} color="var(--color-muted-foreground)" className="animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon="ClipboardList" label="Total Assignments" value={totalAssignments} sub="All time" color="primary" />
        <StatCard icon="CheckCircle" label="Completed" value={totalCompleted} sub="Finished jobs" color="success" />
        <StatCard icon="Briefcase" label="Active" value={totalActive} sub="In progress" color="warning" />
        <StatCard icon="Star" label="Avg Rating" value={avgRating} sub={`${technicians?.length} technicians`} color="primary" />
      </div>
      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Workload Bar Chart */}
        <div className="lg:col-span-2 bg-card rounded-lg border border-border p-5">
          <h3 className="text-base font-heading font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="BarChart2" size={18} color="var(--color-primary)" />
            Technician Workload
          </h3>
          {barData?.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={barData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="name" tick={{ fontSize: 12, fill: 'var(--color-muted-foreground)' }} />
                <YAxis tick={{ fontSize: 12, fill: 'var(--color-muted-foreground)' }} allowDecimals={false} />
                <Tooltip
                  contentStyle={{ background: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: 8, fontSize: 12 }}
                />
                <Bar dataKey="Active" fill="#f59e0b" radius={[3, 3, 0, 0]} />
                <Bar dataKey="Completed" fill="#10b981" radius={[3, 3, 0, 0]} />
                <Bar dataKey="Urgent" fill="#ef4444" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex flex-col items-center justify-center h-48 text-muted-foreground">
              <Icon name="BarChart2" size={36} color="currentColor" className="mb-2 opacity-40" />
              <p className="text-sm">No assignment data yet</p>
            </div>
          )}
        </div>

        {/* Status Pie */}
        <div className="bg-card rounded-lg border border-border p-5">
          <h3 className="text-base font-heading font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="PieChart" size={18} color="var(--color-primary)" />
            Status Breakdown
          </h3>
          {statusPieData?.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={statusPieData} cx="50%" cy="50%" innerRadius={55} outerRadius={80} paddingAngle={3} dataKey="value">
                  {statusPieData?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS?.[index % COLORS?.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ background: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: 8, fontSize: 12 }} />
                <Legend iconType="circle" iconSize={10} wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex flex-col items-center justify-center h-48 text-muted-foreground">
              <Icon name="PieChart" size={36} color="currentColor" className="mb-2 opacity-40" />
              <p className="text-sm">No data yet</p>
            </div>
          )}
        </div>
      </div>
      {/* Location Distribution */}
      {locationStats?.length > 0 && (
        <div className="bg-card rounded-lg border border-border p-5">
          <h3 className="text-base font-heading font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="MapPin" size={18} color="var(--color-primary)" />
            Assignments by Well Location
          </h3>
          <div className="space-y-3">
            {locationStats?.map((loc, i) => {
              const max = locationStats?.[0]?.value || 1;
              const pct = Math.round((loc?.value / max) * 100);
              return (
                <div key={loc?.name} className="flex items-center gap-3">
                  <span className="text-sm text-muted-foreground w-32 truncate">{loc?.name}</span>
                  <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{ width: `${pct}%`, background: COLORS?.[i % COLORS?.length] }}
                    />
                  </div>
                  <span className="text-sm font-medium text-foreground w-6 text-right">{loc?.value}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {/* Technician Performance Table */}
      <div className="bg-card rounded-lg border border-border p-5">
        <h3 className="text-base font-heading font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="Users" size={18} color="var(--color-primary)" />
          Technician Performance
        </h3>
        {techStats?.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-2 px-3 text-xs font-semibold text-muted-foreground">Technician</th>
                  <th className="text-center py-2 px-3 text-xs font-semibold text-muted-foreground">Total</th>
                  <th className="text-center py-2 px-3 text-xs font-semibold text-muted-foreground">Active</th>
                  <th className="text-center py-2 px-3 text-xs font-semibold text-muted-foreground">Completed</th>
                  <th className="text-center py-2 px-3 text-xs font-semibold text-muted-foreground">Urgent</th>
                  <th className="text-center py-2 px-3 text-xs font-semibold text-muted-foreground">Rating</th>
                  <th className="text-center py-2 px-3 text-xs font-semibold text-muted-foreground">Status</th>
                </tr>
              </thead>
              <tbody>
                {techStats?.map(tech => (
                  <tr key={tech?.id} className="border-b border-border hover:bg-muted/30 transition-smooth">
                    <td className="py-3 px-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full overflow-hidden bg-muted flex-shrink-0">
                          <Image src={tech?.avatar} alt={tech?.avatarAlt} className="w-full h-full object-cover" />
                        </div>
                        <span className="text-sm font-medium text-foreground">{tech?.name}</span>
                      </div>
                    </td>
                    <td className="py-3 px-3 text-center">
                      <span className="text-sm font-semibold text-foreground">{tech?.totalAssignments}</span>
                    </td>
                    <td className="py-3 px-3 text-center">
                      <span className="px-2 py-0.5 rounded text-xs font-medium bg-warning/10 text-warning">{tech?.activeAssignments}</span>
                    </td>
                    <td className="py-3 px-3 text-center">
                      <span className="px-2 py-0.5 rounded text-xs font-medium bg-success/10 text-success">{tech?.completedAssignments}</span>
                    </td>
                    <td className="py-3 px-3 text-center">
                      {tech?.urgentAssignments > 0 ? (
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-error/10 text-error">{tech?.urgentAssignments}</span>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="py-3 px-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Icon name="Star" size={12} color="#f59e0b" />
                        <span className="text-sm font-medium text-foreground">{tech?.rating?.toFixed(1)}</span>
                      </div>
                    </td>
                    <td className="py-3 px-3 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        tech?.status === 'available' ? 'bg-success/10 text-success' :
                        tech?.status === 'busy' ? 'bg-warning/10 text-warning' : 'bg-muted text-muted-foreground'
                      }`}>
                        {tech?.status?.charAt(0)?.toUpperCase() + tech?.status?.slice(1)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-10">
            <Icon name="Users" size={40} color="var(--color-muted-foreground)" className="mx-auto mb-3 opacity-40" />
            <p className="text-sm text-muted-foreground">No technician data available</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AssignmentAnalytics;
